import { type Language } from "../lib/translations";
import { Card, CardHeader, CardTitle } from "./ui/card";
import { Calendar } from "lucide-react";
import { ExpandableDescription } from "./ExpandableDescription";

export interface ProjectItem {
  id: string;
  title_en: string;
  title_tr: string;
  date: string;
  version: string;
  tags_en: string; // JSON array
  tags_tr: string; // JSON array
  summary_en: string;
  summary_tr: string;
  detailed_content_en: string;
  detailed_content_tr: string;
  tech_stack: string; // Comma-separated
  links: string; // JSON array: [{"name_en": "", "name_tr": "", "url": ""}]
  images: string; // JSON array of strings
  metrics: string; // JSON array: [{"value_en": "", "value_tr": "", "label_en": "", "label_tr": "", "color": "", "font_size": ""}]
}

function parseJsonArray(jsonStr: string, fallback: any[] = []): any[] {
  try {
    return jsonStr ? JSON.parse(jsonStr) : fallback;
  } catch {
    return fallback;
  }
}

interface ProjectCardProps {
  project: ProjectItem;
  lang: Language;
  onClick: () => void;
}

export function ProjectCard({ project, lang, onClick }: ProjectCardProps) {
  const tags = parseJsonArray(lang === "tr" ? project.tags_tr : project.tags_en);
  return (
    <Card
      onClick={onClick}
      glowing
      className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-primary/10 cursor-pointer flex flex-col justify-between text-left"
    >
      <CardHeader className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase font-mono font-bold text-brand-secondary/80 bg-brand-secondary/5 px-2.5 py-1 rounded border border-border/40">
            {project.version}
          </span>
          <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {project.date}
          </span>
        </div>
        <CardTitle className="text-lg font-extrabold text-foreground group-hover:text-brand-primary transition-colors">
          {lang === "tr" ? project.title_tr : project.title_en}
        </CardTitle>
        <ExpandableDescription
          text={lang === "tr" ? project.summary_tr : project.summary_en}
          maxLines={3}
        />
      </CardHeader>
      <div className="p-5 pt-0 flex flex-wrap gap-1.5">
        {tags.slice(0, 3).map((tag: string, i: number) => (
          <span
            key={i}
            className="text-[9px] uppercase font-bold text-brand-accent bg-brand-accent/5 px-2 py-0.5 rounded-full border border-brand-accent/10"
          >
            {tag}
          </span>
        ))}
      </div>
    </Card>
  );
}
